import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

function markerColor(r) {
  if (r.freshness === "stale" || r.freshness === "no_report") return "#D97706"; // amber
  if (r.available_beds > 0) return "#059669"; // green
  return "#64748b"; // slate — no immediate availability
}

function makeIcon(color, selected) {
  return L.divIcon({
    className: "",
    html: `<div class="latp-pin" style="background:${color};${selected ? "outline:3px solid #0F766E;outline-offset:1px;transform:scale(1.3);" : ""}"></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
}

function statusText(r) {
  if (r.freshness === "stale" || r.freshness === "no_report") return "Verify by phone";
  if (r.available_beds > 0) return "Available now";
  return "No immediate availability";
}

export default function LouisianaMap({ results = [], selectedId, onSelect }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const markersRef = useRef({}); // id -> { marker, color }
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  // init once
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, { scrollWheelZoom: true }).setView([31.0, -92.2], 7);
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // rebuild markers only when the result set changes (not on hover/select)
  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    markersRef.current = {};
    const pts = results.filter((r) => r.lat != null && r.lng != null);
    pts.forEach((r) => {
      const color = markerColor(r);
      const m = L.marker([r.lat, r.lng], { icon: makeIcon(color, r.id === selectedId) }).addTo(layer);
      m.bindPopup(`<strong>${r.facility_name || r.name}</strong><br/>${r.name || ""}<br/><span style="color:${color}">${statusText(r)}</span>`);
      m.on("click", () => onSelectRef.current && onSelectRef.current(r.id));
      markersRef.current[r.id] = { marker: m, color };
    });
    if (pts.length > 0) {
      try {
        map.fitBounds(L.latLngBounds(pts.map((p) => [p.lat, p.lng])).pad(0.2), { maxZoom: 10 });
      } catch (e) {
        /* ignore */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  // highlight the selected marker only — no rebuild, no recenter
  useEffect(() => {
    Object.entries(markersRef.current).forEach(([id, { marker, color }]) => {
      marker.setIcon(makeIcon(color, id === selectedId));
    });
  }, [selectedId]);

  return <div ref={containerRef} data-testid="louisiana-map" className="h-full w-full rounded-lg border border-slate-200" style={{ minHeight: 400 }} />;
}
